import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  Box, Grid, Typography, Paper, Skeleton, Alert, Stack, Chip, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, Button, Drawer, IconButton, TextField, MenuItem,
  InputAdornment, FormHelperText, CircularProgress, Tooltip
} from '@mui/material';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import ReceiptRoundedIcon from '@mui/icons-material/ReceiptRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AccountBalanceRoundedIcon from '@mui/icons-material/AccountBalanceRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import PendingActionsRoundedIcon from '@mui/icons-material/PendingActionsRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import API_BASE_URL from '../../Config/config';
import { useNextGenAuth } from '../context/NextGenAuthContext';
import {
  TOKENS, PageContainer, PageHeader, SectionCard, StatCard,
  EmptyState, GradientButton, StatusChip, fadeUp,
} from '../components/NextGenPortalUI';

const TYPE_CONFIG = {
  credit: {
    icon: <ArrowUpwardRoundedIcon sx={{ fontSize: 13 }} />,
    color: '#22C55E',
    bg: 'rgba(34,197,94,0.1)',
    border: 'rgba(34,197,94,0.2)',
    sign: '+',
  },
  debit: {
    icon: <ArrowDownwardRoundedIcon sx={{ fontSize: 13 }} />,
    color: '#EF4444',
    bg: 'rgba(239,68,68,0.1)',
    border: 'rgba(239,68,68,0.2)',
    sign: '−',
  },
};

const PURPOSE_LABELS = {
  referral_reward: 'Referral Reward',
  subscription_payment: 'Subscription Payment',
  withdrawal: 'Withdrawal',
  refund: 'Refund',
  admin_adjustment: 'Admin Adjustment'
};

const STATUS_CONFIG = {
  pending: { color: '#F59E0B', label: 'Pending' },
  under_review: { color: '#6366F1', label: 'Under Review' },
  approved: { color: '#22C55E', label: 'Approved' },
  rejected: { color: '#EF4444', label: 'Rejected' },
  processing: { color: '#3B82F6', label: 'Processing' },
  completed: { color: '#10B981', label: 'Completed' },
  failed: { color: '#DC2626', label: 'Failed' },
  cancelled: { color: '#6B7280', label: 'Cancelled' },
};

// Maps business withdrawal statuses to the shared StatusChip's color tones.
const STATUS_TONE_MAP = {
  pending: 'pending',
  under_review: 'processing',
  approved: 'success',
  rejected: 'failed',
  processing: 'processing',
  completed: 'success',
  failed: 'failed',
  cancelled: 'default',
};

const BORDER = `1px solid ${TOKENS.border}`;

const NextGenWallet = () => {
  const { nextgen_builder } = useNextGenAuth();
  const [data, setData] = useState(null);
  const [config, setConfig] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const rowsPerPage = 20;

  // Withdrawal Drawer State
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [withdrawalAmount, setWithdrawalAmount] = useState('');
  const [payoutMethod, setPayoutMethod] = useState('');
  const [upiId, setUpiId] = useState('');
  const [bankDetails, setBankDetails] = useState({
    accountHolderName: '',
    bankName: '',
    accountNumber: '',
    confirmAccountNumber: '',
    ifscCode: '',
    branchName: ''
  });

  const fetchData = async () => {
    try {
      const [walletRes, configRes, historyRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/nextgen-builder/wallet/history?page=1&limit=100`),
        axios.get(`${API_BASE_URL}/api/nextgen-builder/wallet/config`),
        axios.get(`${API_BASE_URL}/api/nextgen-builder/wallet/withdrawals`)
      ]);

      // Guarded at the point state is SET rather than at each render site.
      // `withdrawals` alone is read by .length twice and .map once further
      // down; normalising here fixes all of them at once and keeps the
      // guarantee in one place. On the happy path these are already the
      // right shapes, so behaviour is byte-for-byte identical — this only
      // changes what happens when the envelope comes back without `data`
      // (observed: `{success:true}` with no payload), which previously threw
      // "Cannot read properties of undefined (reading 'length')" and took
      // the whole wallet route down.
      setData(walletRes.data?.data ?? null);
      setConfig(configRes.data?.data ?? null);
      setWithdrawals(Array.isArray(historyRes.data?.data) ? historyRes.data.data : []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load wallet data');
    } finally {
      setLoading(false);
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (nextgen_builder?.id) {
      fetchData();
    }
  }, [nextgen_builder?.id]);

  const handleWithdrawalSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError('');

    try {
      const payload = {
        amount: withdrawalAmount,
        payoutMethod,
        upiId: payoutMethod === 'UPI' ? upiId : undefined,
        bankDetails: payoutMethod === 'BANK_TRANSFER' ? bankDetails : undefined
      };

      // Validation
      if (payoutMethod === 'BANK_TRANSFER' && bankDetails.accountNumber !== bankDetails.confirmAccountNumber) {
        throw new Error('Account numbers do not match');
      }

      const res = await axios.post(`${API_BASE_URL}/api/nextgen-builder/wallet/withdraw`, payload);

      if (res.data.success) {
        setSuccessMessage('Your withdrawal request has been submitted successfully.');
        setDrawerOpen(false);
        // Reset form
        setWithdrawalAmount('');
        setPayoutMethod('');
        setUpiId('');
        setBankDetails({
          accountHolderName: '',
          bankName: '',
          accountNumber: '',
          confirmAccountNumber: '',
          ifscCode: '',
          branchName: ''
        });
        // Refresh data
        fetchData();
      }
    } catch (err) {
      setFormError(err.response?.data?.message || err.message || 'Withdrawal request failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelWithdrawal = async (id) => {
    if (!window.confirm('Are you sure you want to cancel this withdrawal request?')) return;

    try {
      await axios.delete(`${API_BASE_URL}/api/nextgen-builder/wallet/withdraw/${id}`);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to cancel withdrawal');
    }
  };

  const transactions = data?.transactions || [];
  const wallet = data?.wallet || { balance: 0, totalEarnings: 0, pendingWithdrawal: 0, totalWithdrawn: 0 };
  const paginatedTx = transactions.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  const minAmount = config?.minWithdrawalAmount || 500;
  const canWithdraw = wallet.balance >= minAmount;

  return (
    <motion.div {...fadeUp}>
      <PageContainer>

        {/* Header */}
        <PageHeader
          icon={<AccountBalanceWalletRoundedIcon />}
          eyebrow="Wallet"
          title="My Wallet"
          description="Your referral earnings and transaction history"
          action={
            <Box sx={{ textAlign: 'right' }}>
              <GradientButton
                disabled={!canWithdraw || loading}
                onClick={() => setDrawerOpen(true)}
                startIcon={<PaymentsRoundedIcon />}
                endIcon={null}
              >
                Withdraw Funds
              </GradientButton>
              {!canWithdraw && !loading && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#DC2626', fontWeight: 600 }}>
                  Min. ₹{minAmount} required to withdraw
                </Typography>
              )}
            </Box>
          }
        />

        {error && <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }}>{error}</Alert>}
        {successMessage && (
          <Alert
            severity="success"
            sx={{ mb: 3, borderRadius: '12px' }}
            onClose={() => setSuccessMessage('')}
          >
            {successMessage}
          </Alert>
        )}

        {/* ── Hero Balance Card ── */}
        <Paper
          elevation={0}
          component={motion.div}
          whileHover={{ y: -2 }}
          transition={{ duration: 0.2 }}
          sx={{
            p: { xs: 3, md: 4 },
            mb: 2.5,
            borderRadius: TOKENS.radius.card,
            background: 'linear-gradient(135deg, rgba(108,59,255,0.12) 0%, rgba(155,111,255,0.05) 100%)',
            border: `1px solid rgba(108,59,255,0.22)`,
            boxShadow: TOKENS.shadow.card,
            position: 'relative',
            overflow: 'hidden',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
            <Box
              sx={{
                width: 64, height: 64, borderRadius: '18px',
                background: TOKENS.brand.gradient,
                color: '#FFFFFF',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: TOKENS.shadow.brand,
                flexShrink: 0,
              }}
            >
              <AccountBalanceWalletRoundedIcon sx={{ fontSize: 32 }} />
            </Box>
            <Box>
              <Typography
                variant="overline"
                sx={{ color: TOKENS.brand.primaryDark, fontSize: '0.72rem', letterSpacing: '0.1em', fontWeight: 700 }}
              >
                Available Balance
              </Typography>
              {loading ? (
                <Skeleton variant="text" height={64} width={180} />
              ) : (
                <Typography
                  sx={{
                    fontWeight: 900, color: TOKENS.text.primary, letterSpacing: '-0.03em',
                    lineHeight: 1, fontSize: { xs: '2.25rem', sm: '2.75rem', md: '3rem' },
                  }}
                >
                  ₹{wallet.balance.toLocaleString('en-IN')}
                </Typography>
              )}
              <Typography variant="body2" sx={{ color: TOKENS.text.secondary, fontWeight: 600, mt: 0.75 }}>
                Ready to withdraw to your bank or UPI
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* ── Balance Cards ── */}
        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              icon={<TrendingUpRoundedIcon />}
              label="Lifetime Earnings"
              value={loading ? '' : `₹${wallet.totalEarnings.toLocaleString('en-IN')}`}
              color={TOKENS.brand.secondary}
              loading={loading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              icon={<PendingActionsRoundedIcon />}
              label="Pending Withdrawals"
              value={loading ? '' : `₹${(wallet.pendingWithdrawal || 0).toLocaleString('en-IN')}`}
              color={TOKENS.accent.orange}
              loading={loading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              icon={<CheckCircleRoundedIcon />}
              label="Total Withdrawn"
              value={loading ? '' : `₹${(wallet.totalWithdrawn || 0).toLocaleString('en-IN')}`}
              color={TOKENS.accent.cyan}
              loading={loading}
            />
          </Grid>
        </Grid>

        {/* ── Withdrawal History ── */}
        <SectionCard sx={{ p: 0, overflow: 'hidden', mb: 3 }}>
          <Box sx={{ px: { xs: 2.5, md: 3 }, py: 2.25, display: 'flex', alignItems: 'center', gap: 1 }}>
            <HistoryRoundedIcon sx={{ color: TOKENS.brand.primaryLight, fontSize: 20 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: TOKENS.text.primary }}>
              Withdrawal History
            </Typography>
            {!historyLoading && withdrawals.length > 0 && (
              <Chip
                label={`${withdrawals.length} requests`}
                size="small"
                sx={{
                  ml: 'auto',
                  bgcolor: 'rgba(108,59,255,0.08)',
                  color: '#6C3BFF',
                  border: '1px solid rgba(108,59,255,0.2)',
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  height: 22,
                }}
              />
            )}
          </Box>
          <Divider sx={{ borderColor: TOKENS.border }} />

          {historyLoading ? (
            <Box sx={{ p: 2.5 }}>
              <Skeleton variant="rectangular" height={100} sx={{ borderRadius: '10px' }} />
            </Box>
          ) : withdrawals.length === 0 ? (
            <EmptyState
              icon={<HistoryRoundedIcon />}
              title="No Withdrawal Requests Yet"
              description="Once you request a payout, you'll be able to track its status here."
              minHeight={200}
            />
          ) : (
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table sx={{ minWidth: 640 }}>
                <TableHead>
                  <TableRow>
                    {['ID', 'Date', 'Amount', 'Method', 'Status', 'Actions'].map((h) => (
                      <TableCell
                        key={h}
                        sx={{
                          color: TOKENS.text.muted, fontWeight: 700, fontSize: '0.72rem',
                          letterSpacing: '0.04em', textTransform: 'uppercase',
                          borderColor: TOKENS.border, whiteSpace: 'nowrap',
                        }}
                      >
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {withdrawals.map((w) => {
                    const status = STATUS_CONFIG[w.status] || { color: '#374151', label: w.status };
                    return (
                      <TableRow
                        key={w.id}
                        sx={{ '&:hover': { bgcolor: 'rgba(108,59,255,0.04)' }, '&:last-child td': { borderBottom: 0 } }}
                      >
                        <TableCell sx={{ fontWeight: 600, color: TOKENS.text.primary, borderColor: TOKENS.border, py: 1.75 }}>
                          {w.request_number}
                        </TableCell>
                        <TableCell sx={{ color: TOKENS.text.secondary, borderColor: TOKENS.border, py: 1.75 }}>
                          {new Date(w.requested_at).toLocaleDateString('en-IN', {
                            day: '2-digit', month: 'short'
                          })}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, color: TOKENS.text.primary, borderColor: TOKENS.border, py: 1.75 }}>
                          ₹{parseFloat(w.amount).toLocaleString('en-IN')}
                        </TableCell>
                        <TableCell sx={{ color: TOKENS.text.secondary, borderColor: TOKENS.border, py: 1.75 }}>
                          {w.payout_method}
                        </TableCell>
                        <TableCell sx={{ borderColor: TOKENS.border, py: 1.75 }}>
                          <StatusChip
                            label={status.label}
                            tone={STATUS_TONE_MAP[w.status] || 'default'}
                            sx={w.status === 'cancelled' ? {
                              bgcolor: 'rgba(107,114,128,0.12)', color: '#6B7280', border: '1px solid rgba(107,114,128,0.28)',
                            } : {}}
                          />
                        </TableCell>
                        <TableCell sx={{ borderColor: TOKENS.border, py: 1.75 }}>
                          {w.status === 'pending' && (
                            <Button
                              size="small"
                              color="error"
                              onClick={() => handleCancelWithdrawal(w.id)}
                              sx={{ textTransform: 'none', fontWeight: 700 }}
                            >
                              Cancel
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </SectionCard>

        {/* ── Transaction History ── */}
        <SectionCard sx={{ p: 0, overflow: 'hidden' }}>
          <Box sx={{ px: { xs: 2.5, md: 3 }, py: 2.25, display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReceiptRoundedIcon sx={{ color: TOKENS.brand.primaryLight, fontSize: 20 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: TOKENS.text.primary }}>
              Transaction History
            </Typography>
            {!loading && transactions.length > 0 && (
              <Chip
                label={`${transactions.length} transactions`}
                size="small"
                sx={{
                  ml: 'auto',
                  bgcolor: 'rgba(108,59,255,0.08)',
                  color: '#6C3BFF',
                  border: '1px solid rgba(108,59,255,0.2)',
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  height: 22,
                }}
              />
            )}
          </Box>
          <Divider sx={{ borderColor: TOKENS.border }} />

          {loading ? (
            <Box sx={{ p: 2.5 }}>
              <Stack spacing={1.25}>
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} variant="rectangular" height={56} sx={{ borderRadius: '10px' }} />
                ))}
              </Stack>
            </Box>
          ) : transactions.length === 0 ? (
            <EmptyState
              icon={<AccountBalanceWalletRoundedIcon />}
              title="No Transactions Yet"
              description="Refer friends to start earning wallet credits."
            />
          ) : (
            <>
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table sx={{ minWidth: 680 }}>
                  <TableHead>
                    <TableRow>
                      {['Type', 'Description', 'Amount', 'Status', 'Date'].map((h) => (
                        <TableCell
                          key={h}
                          sx={{
                            color: TOKENS.text.muted, fontWeight: 700, fontSize: '0.72rem',
                            letterSpacing: '0.04em', textTransform: 'uppercase',
                            borderColor: TOKENS.border, whiteSpace: 'nowrap',
                          }}
                        >
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedTx.map((tx) => {
                      const cfg = TYPE_CONFIG[tx.type] || TYPE_CONFIG.credit;
                      return (
                        <TableRow
                          key={tx.id}
                          sx={{ '&:hover': { bgcolor: 'rgba(108,59,255,0.04)' }, '&:last-child td': { borderBottom: 0 } }}
                        >
                          <TableCell sx={{ borderColor: TOKENS.border, py: 1.75 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box
                                sx={{
                                  p: 0.6, borderRadius: '7px',
                                  bgcolor: cfg.bg, border: `1px solid ${cfg.border}`,
                                  color: cfg.color, display: 'flex',
                                }}
                              >
                                {cfg.icon}
                              </Box>
                              <Chip
                                label={PURPOSE_LABELS[tx.purpose] || tx.purpose}
                                size="small"
                                sx={{
                                  bgcolor: `${cfg.color}0E`,
                                  color: cfg.color,
                                  border: `1px solid ${cfg.color}22`,
                                  fontWeight: 600,
                                  fontSize: '0.68rem',
                                  height: 20,
                                }}
                              />
                            </Box>
                          </TableCell>
                          <TableCell sx={{ maxWidth: 220, borderColor: TOKENS.border, py: 1.75 }}>
                            <Typography
                              variant="caption"
                              sx={{
                                color: TOKENS.text.secondary,
                                display: 'block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                fontWeight: 500,
                              }}
                            >
                              {tx.description || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ borderColor: TOKENS.border, py: 1.75 }}>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 800, color: cfg.color, fontSize: '0.9rem', letterSpacing: '-0.01em' }}
                            >
                              {cfg.sign}₹{parseFloat(tx.amount).toLocaleString('en-IN')}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ borderColor: TOKENS.border, py: 1.75 }}>
                            <StatusChip
                              label={tx.status || 'completed'}
                              tone={tx.status === 'completed' ? 'success' : 'pending'}
                            />
                          </TableCell>
                          <TableCell sx={{ borderColor: TOKENS.border, py: 1.75 }}>
                            <Typography variant="caption" sx={{ color: TOKENS.text.muted, fontWeight: 500 }}>
                              {new Date(tx.created_at).toLocaleDateString('en-IN', {
                                day: '2-digit', month: 'short', year: 'numeric',
                              })}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {transactions.length > rowsPerPage && (
                <TablePagination
                  component="div"
                  count={transactions.length}
                  page={page}
                  rowsPerPage={rowsPerPage}
                  rowsPerPageOptions={[20]}
                  onPageChange={(_, newPage) => setPage(newPage)}
                />
              )}
            </>
          )}
        </SectionCard>
      </PageContainer>

      {/* ── Withdrawal Drawer ── */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: { width: { xs: '100%', sm: 400 }, p: 0, borderRadius: { xs: 0, sm: '20px 0 0 20px' } }
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Drawer Header */}
          <Box sx={{ p: 3, borderBottom: BORDER, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#111827' }}>
                Withdraw Funds
              </Typography>
              <Typography variant="caption" sx={{ color: '#374151', fontWeight: 600 }}>
                Request a payout to your preferred method
              </Typography>
            </Box>
            <IconButton onClick={() => setDrawerOpen(false)} size="small">
              <CloseRoundedIcon />
            </IconButton>
          </Box>

          {/* Drawer Body */}
          <Box sx={{ p: 3, flexGrow: 1, overflowY: 'auto' }} component="form" onSubmit={handleWithdrawalSubmit}>
            <Stack spacing={3}>
              {/* Info Box */}
              <Box sx={{ p: 2, bgcolor: 'rgba(108,59,255,0.05)', borderRadius: '12px', border: '1px solid rgba(108,59,255,0.1)' }}>
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <AccountBalanceRoundedIcon sx={{ color: '#6C3BFF', fontSize: 20 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#6C3BFF' }}>
                    Payout Summary
                  </Typography>
                </Box>
                <Grid container spacing={1}>
                  <Grid item xs={7}>
                    <Typography variant="caption" sx={{ color: '#374151', fontWeight: 600 }}>Available for Withdrawal</Typography>
                  </Grid>
                  <Grid item xs={5} sx={{ textAlign: 'right' }}>
                    <Typography variant="caption" sx={{ color: '#111827', fontWeight: 800 }}>₹{wallet.balance.toLocaleString('en-IN')}</Typography>
                  </Grid>
                  <Grid item xs={7}>
                    <Typography variant="caption" sx={{ color: '#374151', fontWeight: 600 }}>Minimum Withdrawal</Typography>
                  </Grid>
                  <Grid item xs={5} sx={{ textAlign: 'right' }}>
                    <Typography variant="caption" sx={{ color: '#111827', fontWeight: 800 }}>₹{minAmount}</Typography>
                  </Grid>
                </Grid>
              </Box>

              {formError && <Alert severity="error" sx={{ borderRadius: '10px' }}>{formError}</Alert>}

              {/* Amount Field */}
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#4B5563', mb: 1, display: 'block' }}>
                  Withdrawal Amount *
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Enter amount"
                  type="number"
                  value={withdrawalAmount}
                  onChange={(e) => setWithdrawalAmount(e.target.value)}
                  required
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    sx: { borderRadius: '12px', fontWeight: 700 }
                  }}
                  helperText={`Max: ₹${wallet.balance}`}
                />
              </Box>

              {/* Payout Method */}
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#4B5563', mb: 1, display: 'block' }}>
                  Payout Method *
                </Typography>
                <TextField
                  select
                  fullWidth
                  value={payoutMethod}
                  onChange={(e) => setPayoutMethod(e.target.value)}
                  required
                  InputProps={{ sx: { borderRadius: '12px', fontWeight: 600 } }}
                >
                  {(Array.isArray(config?.payoutMethods) ? config.payoutMethods : []).map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              <AnimatePresence mode="wait">
                {payoutMethod === 'UPI' && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#4B5563', mb: 1, display: 'block' }}>
                      UPI ID *
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="username@bank"
                      value={upiId}
                      onChange={(e) => setUpiId(e.target.value)}
                      required
                      InputProps={{ sx: { borderRadius: '12px' } }}
                      helperText="Examples: user@okaxis, user@ybl, user@oksbi"
                    />
                  </motion.div>
                )}

                {payoutMethod === 'BANK_TRANSFER' && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                  >
                    <Stack spacing={2.5}>
                      <Box>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#4B5563', mb: 1, display: 'block' }}>
                          Account Holder Name *
                        </Typography>
                        <TextField
                          fullWidth
                          value={bankDetails.accountHolderName}
                          onChange={(e) => setBankDetails({ ...bankDetails, accountHolderName: e.target.value })}
                          required
                          InputProps={{ sx: { borderRadius: '12px' } }}
                        />
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#4B5563', mb: 1, display: 'block' }}>
                          Bank Name *
                        </Typography>
                        <TextField
                          fullWidth
                          value={bankDetails.bankName}
                          onChange={(e) => setBankDetails({ ...bankDetails, bankName: e.target.value })}
                          required
                          InputProps={{ sx: { borderRadius: '12px' } }}
                        />
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#4B5563', mb: 1, display: 'block' }}>
                          Account Number *
                        </Typography>
                        <TextField
                          fullWidth
                          type="password"
                          value={bankDetails.accountNumber}
                          onChange={(e) => setBankDetails({ ...bankDetails, accountNumber: e.target.value })}
                          required
                          InputProps={{ sx: { borderRadius: '12px' } }}
                        />
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#4B5563', mb: 1, display: 'block' }}>
                          Confirm Account Number *
                        </Typography>
                        <TextField
                          fullWidth
                          value={bankDetails.confirmAccountNumber}
                          onChange={(e) => setBankDetails({ ...bankDetails, confirmAccountNumber: e.target.value })}
                          required
                          InputProps={{ sx: { borderRadius: '12px' } }}
                          error={bankDetails.accountNumber !== bankDetails.confirmAccountNumber && bankDetails.confirmAccountNumber !== ''}
                          helperText={bankDetails.accountNumber !== bankDetails.confirmAccountNumber && bankDetails.confirmAccountNumber !== '' ? 'Account numbers do not match' : ''}
                        />
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#4B5563', mb: 1, display: 'block' }}>
                          IFSC Code *
                        </Typography>
                        <TextField
                          fullWidth
                          placeholder="e.g. SBIN0001234"
                          value={bankDetails.ifscCode}
                          onChange={(e) => setBankDetails({ ...bankDetails, ifscCode: e.target.value.toUpperCase() })}
                          required
                          InputProps={{ sx: { borderRadius: '12px' } }}
                        />
                      </Box>
                    </Stack>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Security Note */}
              <Box sx={{ display: 'flex', gap: 1, p: 1.5, bgcolor: '#F8FAFC', borderRadius: '10px' }}>
                <InfoOutlinedIcon sx={{ color: '#374151', fontSize: 16 }} />
                <Typography variant="caption" sx={{ color: '#374151', lineHeight: 1.4 }}>
                  Your banking details are encrypted and stored securely. We never share your data with third parties.
                </Typography>
              </Box>
            </Stack>
          </Box>

          {/* Drawer Footer */}
          <Box sx={{ p: 3, borderTop: BORDER, bgcolor: TOKENS.bg.page }}>
            <GradientButton
              fullWidth
              type="submit"
              disabled={submitting || !payoutMethod || !withdrawalAmount}
              endIcon={null}
              sx={{ py: 1.5, fontSize: '1rem' }}
              onClick={handleWithdrawalSubmit}
            >
              {submitting ? <CircularProgress size={24} sx={{ color: '#FFF' }} /> : 'Submit Withdrawal Request'}
            </GradientButton>
            <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 1.5, color: TOKENS.text.muted, fontWeight: 500 }}>
              Estimated processing time: {config?.processingTime}
            </Typography>
          </Box>
        </Box>
      </Drawer>
    </motion.div>
  );
};

export default NextGenWallet;
